import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCategoryParam } from '../../hooks/useCategoryParam';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import AddressSelector from '../../components/customer/AddressSelector';
import { toast } from 'sonner';
import {
  Search, Plus, Minus, ChevronDown, ChevronRight, User,
  Home, ShoppingBag, Grid3X3, ShoppingCart, Zap,
  Sun, Headphones, Sparkles, Heart, Pill, Package, CalendarCheck
} from 'lucide-react';
import axios from 'axios';
import { PromoBannerCarousel } from '../../components/customer/PromoBannerCarousel';
import { CouponStrip } from '../../components/customer/CouponStrip';
import { PromotionStrip } from '../../components/customer/PromotionStrip';
import { usePromotions } from '../../hooks/usePromotions';
import { UnavailableBadge, isUnavailable, isVariantUnavailable } from '../../components/customer/UnavailableBadge';
import { ComingSoonButton } from '../../components/customer/ComingSoonButton';
import { isComingSoon } from '../../utils/comingSoon';
import { ProductImageCarousel } from '../../components/customer/ProductImageCarousel';
import { SubCategoryFilter } from '../../components/customer/SubCategoryFilter';
import { VariantPicker } from '../../components/customer/VariantPicker';
import { CustomerDesktopNav } from '../../components/customer/CustomerDesktopNav';
import { CUSTOMER_PAGE, PRODUCT_GRID } from '../../components/customer/customerLayout';
import { isNavItemActive, useCustomerNavItems } from '../../components/customer/customerNav';
import { ScrollRow } from '../../components/customer/ScrollRow';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEARCH_HINTS = [
  '"board games"', '"cold drinks"', '"paneer"', '"shampoo"', '"chips"', '"bread"',
];

const BlinkitSearch = () => {
  const [idx, setIdx] = useState(0);
  const [fade, setFade] = useState(true);
  useEffect(() => {
    const t = setInterval(() => {
      setFade(false);
      setTimeout(() => { setIdx(i => (i + 1) % SEARCH_HINTS.length); setFade(true); }, 200);
    }, 2400);
    return () => clearInterval(t);
  }, []);
  return (
    <span className="text-gray-500 text-sm pointer-events-none transition-all duration-200"
      style={{ opacity: fade ? 1 : 0 }}>
      Search {SEARCH_HINTS[idx]}
    </span>
  );
};

const BlinkitHome = () => {
  const navigate = useNavigate();
  const { user, storeSettings } = useAuth();
  const { addItem, getItemQuantity, updateQuantity, totalItems, totalAmount } = useCart();

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  // In the URL, not local state, so Back from a product returns to this tab.
  const [selectedTab, setSelectedTab] = useCategoryParam();
  const [selectedSubCategories, setSelectedSubCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [promoBanners, setPromoBanners] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const promotions = usePromotions();
  const [selectedDeliveryAddress, setSelectedDeliveryAddress] = useState(null);
  // Serviceable Apartments: once the tenant uses the apartment-first address
  // flow (feature on + at least one apartment configured — same gating as
  // MapAddressPicker/DeliveryOrders/AdminOrders), delivery is always to one
  // known apartment/flat rather than a choice between several saved
  // addresses, so the generic multi-address switcher doesn't apply here.
  const isApartmentsEnabled = storeSettings?.tenant_features?.serviceable_apartments === true;
  const [hasApartments, setHasApartments] = useState(false);
  useEffect(() => {
    if (!isApartmentsEnabled) return;
    axios.get(`${API}/admin/serviceable-apartments`)
      .then(res => setHasApartments((res.data || []).length > 0))
      .catch(() => {});
  }, [isApartmentsEnabled]);
  const apartmentFlowActive = isApartmentsEnabled && hasApartments;
  // Same mode check already used for the delivery-date/slot picker in
  // CustomerCart.jsx — an unset regular_order_mode is treated as instant
  // (the tenant's long-standing default before slot-based mode existed).
  const isInstantDelivery = storeSettings?.regular_order_mode === 'instant' || !storeSettings?.regular_order_mode;
  const searchRef = useRef(null);

  const sessionId = useRef(
    sessionStorage.getItem('_sid') ||
    (() => { const s = Math.random().toString(36).slice(2); sessionStorage.setItem('_sid', s); return s; })()
  );

  useEffect(() => { loadData(); trackPageview(); }, [storeSettings?.admin_id]);

  const loadData = async () => {
    try {
      const adminId = user?.admin_id || storeSettings?.admin_id;
      let reqs;
      if (user && user.admin_id) {
        reqs = [
          axios.get(`${API}/admin/categories`),
          axios.get(`${API}/customer/products`),
        ];
        if (user.admin_id) {
          reqs.push(axios.get(`${API}/store/${user.admin_id}/promo-banners`).catch(() => ({ data: [] })));
          reqs.push(axios.get(`${API}/store/${user.admin_id}/coupons`).catch(() => ({ data: [] })));
        }
      } else if (adminId) {
        reqs = [
          axios.get(`${API}/public/store/${adminId}/categories`).catch(() => ({ data: [] })),
          axios.get(`${API}/public/store/${adminId}/products`).catch(() => ({ data: [] })),
          axios.get(`${API}/store/${adminId}/promo-banners`).catch(() => ({ data: [] })),
          axios.get(`${API}/store/${adminId}/coupons`).catch(() => ({ data: [] })),
        ];
      } else {
        setLoading(false);
        return;
      }
      const results = await Promise.all(reqs);
      setCategories(results[0].data);
      setProducts(results[1].data);
      if (results[2]) setPromoBanners(results[2].data);
      if (results[3]) setCoupons(results[3].data);
    } catch (e) { console.error('Load error:', e); }
    finally { setLoading(false); }
  };

  const trackPageview = () => {
    if (!user?.admin_id) return;
    axios.post(`${API}/track/pageview`, {
      admin_id: user.admin_id, customer_id: user.id,
      session_id: sessionId.current, page: '/home', source: 'direct',
    }).catch(() => {});
  };

  const handleAdd = (product, selectedVariant = null) => {
    if (!user) {
      navigate('/login');
      toast.info('Please login to add items to cart');
      return;
    }
    addItem(product, 1, selectedVariant);
    const label = selectedVariant ? `${product.name} (${selectedVariant.label})` : product.name;
    toast.success(`Added ${label}`);
  };

  const handleSelectTab = (tabId) => {
    setSelectedTab(tabId);
    setSelectedSubCategories([]);
  };

  const curr = storeSettings?.currency_symbol || '₹';
  const autoPlay = storeSettings?.auto_image_carousel !== false;
  const detailEnabled = storeSettings?.product_detail_page !== false;
  const filteredProducts = (selectedTab
    ? products.filter(p => {
        if (p.category_id !== selectedTab) return false;
        if (selectedSubCategories.length > 0 && !selectedSubCategories.includes(p.sub_category_id)) return false;
        return true;
      })
    : products
  );
  const searchedProducts = searchQuery.trim()
    ? products.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : null;

  // Group products by category for "Bestsellers" grid
  const catProductMap = {};
  products.forEach(p => {
    if (!catProductMap[p.category_id]) catProductMap[p.category_id] = [];
    catProductMap[p.category_id].push(p);
  });

  const catIcons = [ShoppingBag, Sun, Headphones, Sparkles, Heart, Pill, Grid3X3, Zap];

  return (
    <div className="min-h-screen bg-white pb-24 lg:pb-10" data-testid="blinkit-home">
      {/* ===== YELLOW HEADER ===== */}
      <div className="blinkit-header sticky top-0 z-50" data-testid="blinkit-header">
        <div className={`${CUSTOMER_PAGE} px-4 pt-3 pb-1`}>
          {/* Delivery info */}
          <div className="flex items-center justify-between mb-3">
            <div>
              {isInstantDelivery ? (
                <>
                  <p className="text-xs font-medium text-[#1A1A1A] opacity-70">
                    {storeSettings?.store_name || 'Blinkit'} in
                  </p>
                  <h1 className="font-heading font-black text-2xl text-[#1A1A1A] leading-tight">
                    14 minutes
                  </h1>
                </>
              ) : (
                <>
                  <p className="text-xs font-medium text-[#1A1A1A] opacity-70">
                    {storeSettings?.store_name || 'Blinkit'}
                  </p>
                  <h1 className="font-heading font-black text-2xl text-[#1A1A1A] leading-tight" data-testid="blinkit-slot-based-label">
                    Slot-Based Delivery
                  </h1>
                </>
              )}
              {!apartmentFlowActive && (
                <div className="mt-0.5 max-w-[220px]" data-testid="blinkit-address">
                  <AddressSelector
                    selectedAddress={selectedDeliveryAddress}
                    onSelect={setSelectedDeliveryAddress}
                  />
                </div>
              )}
            </div>
            <CustomerDesktopNav className="ml-auto" activeClassName="text-[#1A1A1A] bg-white/70" cartClassName="bg-[#2D8C3C] text-white" />
            <Link to="/profile" className="lg:hidden w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm" data-testid="blinkit-profile">
              <User className="w-5 h-5 text-[#1A1A1A]" />
            </Link>
          </div>

          {/* Search bar */}
          <div className={`bg-[#FFFDF0] border rounded-xl px-4 py-3 flex items-center gap-3 mb-3 transition-all ${searchFocused ? 'border-[#E5B200] ring-1 ring-[#F7C948]/30' : 'border-transparent'}`}
            onClick={() => searchRef.current?.focus()} data-testid="blinkit-search">
            <Search className="w-5 h-5 text-gray-500 shrink-0" />
            <div className="flex-1 relative">
              {/* Input stays in flow so it defines this wrapper's height — see ZeptoHome. */}
              {!searchFocused && !searchQuery && (
                <div className="absolute inset-0 flex items-center pointer-events-none">
                  <BlinkitSearch />
                </div>
              )}
              <input ref={searchRef} value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                onFocus={() => setSearchFocused(true)} onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
                className="w-full bg-transparent outline-none text-sm text-[#1A1A1A]"
                data-testid="blinkit-search-input" />
            </div>
          </div>

          {/* Category tabs with icons */}
          <div className="flex gap-5 overflow-x-auto scrollbar-hide pb-2" data-testid="blinkit-category-tabs">
            <button onClick={() => handleSelectTab(null)}
              className="flex flex-col items-center gap-1 shrink-0 min-w-[52px]"
              data-testid="blinkit-tab-all">
              <ShoppingBag className={`w-6 h-6 ${!selectedTab ? 'text-[#1A1A1A]' : 'text-gray-400'}`} />
              <span className={`text-[11px] ${!selectedTab ? 'font-bold text-[#1A1A1A]' : 'font-medium text-gray-500'}`}>All</span>
              {!selectedTab && <div className="w-8 h-[3px] bg-[#1A1A1A] rounded-full" />}
            </button>
            {categories.slice(0, 5).map((cat, i) => {
              const Icon = catIcons[(i + 1) % catIcons.length];
              const active = selectedTab === cat.id;
              return (
                <button key={cat.id} onClick={() => handleSelectTab(cat.id)}
                  className="flex flex-col items-center gap-1 shrink-0 min-w-[52px] relative"
                  data-testid={`blinkit-tab-${cat.id}`}>
                  <div className="relative">
                    {cat.image_url ? (
                      <div className="w-6 h-6 rounded-full overflow-hidden">
                        <img src={cat.image_url} alt={cat.name} className="w-full h-full object-cover" />
                      </div>
                    ) : (
                      <Icon className={`w-6 h-6 ${active ? 'text-[#1A1A1A]' : 'text-gray-400'}`} />
                    )}
                  </div>
                  <span className={`text-[11px] text-center line-clamp-1 ${active ? 'font-bold text-[#1A1A1A]' : 'font-medium text-gray-500'}`}>{cat.name}</span>
                  {active && <div className="w-8 h-[3px] bg-[#1A1A1A] rounded-full" />}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className={CUSTOMER_PAGE}>
        {/* ===== SEARCH RESULTS ===== */}
        {searchedProducts && (
          <div className="px-4 py-4" data-testid="blinkit-search-results">
            <p className="text-xs text-gray-500 mb-3">
              {searchedProducts.length} result{searchedProducts.length !== 1 ? 's' : ''} for "{searchQuery}"
            </p>
            <div className={PRODUCT_GRID}>
              {searchedProducts.slice(0, 8).map(p => (
                <BlinkitProductGrid key={p.id} product={p} curr={curr}
                  getItemQuantity={getItemQuantity} onAdd={(v) => handleAdd(p, v)} onAddBase={() => handleAdd(p)}
                  onUpdate={(cartKey, qty) => updateQuantity(cartKey, qty)} storeSettings={storeSettings} autoPlay={autoPlay} />
              ))}
            </div>
            {searchedProducts.length === 0 && <p className="text-center py-8 text-gray-400 text-sm">No products found</p>}
          </div>
        )}

        {/* ===== MAIN CONTENT ===== */}
        {!searchedProducts && (
          <>
            {/* Category selected → filtered grid */}
            {selectedTab ? (
              <div className="px-4 pt-4 pb-6" data-testid="blinkit-cat-products">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-heading font-bold text-lg text-[#1A1A1A]">
                    {categories.find(c => c.id === selectedTab)?.name}
                  </h3>
                  <span className="text-xs text-gray-400">{filteredProducts.length} items</span>
                </div>
                <SubCategoryFilter
                  categories={categories}
                  selectedTab={selectedTab}
                  selectedSubCategories={selectedSubCategories}
                  onSelect={setSelectedSubCategories}
                />
                {loading ? (
                  <div className={PRODUCT_GRID}>{[1,2,3,4].map(i => <div key={i} className="h-48 bg-gray-100 rounded-xl animate-pulse" />)}</div>
                ) : filteredProducts.length === 0 ? (
                  <p className="text-center py-8 text-gray-400 text-sm">No products here yet</p>
                ) : (
                  <div className={PRODUCT_GRID}>
                    {filteredProducts.map(p => (
                      <BlinkitProductGrid key={p.id} product={p} curr={curr}
                        getItemQuantity={getItemQuantity} onAdd={(v) => handleAdd(p, v)} onAddBase={() => handleAdd(p)}
                        onUpdate={(cartKey, qty) => updateQuantity(cartKey, qty)} storeSettings={storeSettings} autoPlay={autoPlay} />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Admin Promo Banners */}
                <div className="pt-3">
                  <PromoBannerCarousel banners={promoBanners} variant="blinkit" />
                </div>

                {/* Coupons */}
                {/* Promotions (automatic discounts) */}
                <PromotionStrip promotions={promotions} variant="blinkit" />

                <CouponStrip coupons={coupons} variant="blinkit" currencySymbol={curr} />

                {/* ===== BESTSELLERS - 3 col category grid with product image grids ===== */}
                {categories.length > 0 && (
                  <div className="px-4 pt-4 pb-2" data-testid="blinkit-bestsellers">
                    <h3 className="font-heading font-bold text-lg text-[#1A1A1A] mb-3">Bestsellers</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                      {categories.slice(0, 6).map(cat => {
                        const catProds = catProductMap[cat.id] || [];
                        const displayProds = catProds.slice(0, 4);
                        const moreCount = Math.max(0, catProds.length - 4);
                        return (
                          <button key={cat.id} onClick={() => handleSelectTab(cat.id)}
                            className="blinkit-bestseller-card" data-testid={`blinkit-best-${cat.id}`}>
                            <div className="blinkit-img-grid">
                              {displayProds.length > 0 ? displayProds.map((p, i) => (
                                p.image_url ? (
                                  <img key={i} src={p.image_url} alt={p.name} />
                                ) : (
                                  <div key={i} className="blinkit-img-placeholder flex items-center justify-center bg-gray-200">
                                    <span className="text-lg font-bold text-gray-400">{p.name.charAt(0)}</span>
                                  </div>
                                )
                              )) : (
                                <>
                                  {[0,1,2,3].map(i => (
                                    <div key={i} className="blinkit-img-placeholder flex items-center justify-center bg-gray-100">
                                      <span className="text-sm font-bold text-gray-300">{cat.name.charAt(0)}</span>
                                    </div>
                                  ))}
                                </>
                              )}
                            </div>
                            {moreCount > 0 && <span className="blinkit-more-badge">+{moreCount} more</span>}
                            <span className="text-[11px] font-bold text-[#1A1A1A] text-center leading-tight mt-1 line-clamp-2">
                              {cat.name}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ===== GROCERY & KITCHEN - Horizontal scroll ===== */}
                {products.length > 0 && (
                  <div className="mt-5 mb-4" data-testid="blinkit-grocery-section">
                    <div className="flex items-center justify-between px-4 mb-3">
                      <h3 className="font-heading font-bold text-lg text-[#1A1A1A]">Grocery & Kitchen</h3>
                      <Link to="/products" className="text-xs text-gray-500 font-medium flex items-center gap-0.5">
                        See all <ChevronRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                    <ScrollRow className="flex gap-3 overflow-x-auto scrollbar-hide px-4 pb-3 snap-x scroll-px-4" testId="blinkit-grocery-scroll">
                      {products.slice(0, 12).map(p => (
                        <BlinkitProductCarousel key={p.id} product={p} curr={curr}
                          getItemQuantity={getItemQuantity} onAdd={(v) => handleAdd(p, v)} onAddBase={() => handleAdd(p)}
                          onUpdate={(cartKey, qty) => updateQuantity(cartKey, qty)} storeSettings={storeSettings} autoPlay={autoPlay} />
                      ))}
                    </ScrollRow>
                  </div>
                )}

                {/* ===== DEALS SECTION ===== */}
                {products.filter(p => p.mrp > p.price).length > 0 && (
                  <div className="mb-4" data-testid="blinkit-deals">
                    <div className="flex items-center justify-between px-4 mb-3">
                      <h3 className="font-heading font-bold text-lg text-[#1A1A1A]">Deals & Offers</h3>
                    </div>
                    <ScrollRow className="flex gap-3 overflow-x-auto scrollbar-hide px-4 pb-3 snap-x scroll-px-4">
                      {products.filter(p => p.mrp > p.price).slice(0, 10).map(p => (
                        <BlinkitProductCarousel key={p.id} product={p} curr={curr}
                          getItemQuantity={getItemQuantity} onAdd={(v) => handleAdd(p, v)} onAddBase={() => handleAdd(p)}
                          onUpdate={(cartKey, qty) => updateQuantity(cartKey, qty)} storeSettings={storeSettings} autoPlay={autoPlay} />
                      ))}
                    </ScrollRow>
                  </div>
                )}

                {/* ===== ALL PRODUCTS ===== */}
                <div className="px-4 pb-6" data-testid="blinkit-all-products">
                  <h3 className="font-heading font-bold text-lg text-[#1A1A1A] mb-3">All Products</h3>
                  {loading ? (
                    <div className={PRODUCT_GRID}>{[1,2,3,4].map(i => <div key={i} className="h-48 bg-gray-100 rounded-xl animate-pulse" />)}</div>
                  ) : (
                    <div className={PRODUCT_GRID}>
                      {products.slice(0, 20).map(p => (
                        <BlinkitProductGrid key={p.id} product={p} curr={curr}
                          getItemQuantity={getItemQuantity} onAdd={(v) => handleAdd(p, v)} onAddBase={() => handleAdd(p)}
                          onUpdate={(cartKey, qty) => updateQuantity(cartKey, qty)} storeSettings={storeSettings} autoPlay={autoPlay} />
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Reserves scroll space for the floating cart bar below (which is `fixed` and
          so doesn't occupy flow height on its own) — without this the last product
          card ends up partially hidden behind it. */}
      {totalItems > 0 && <div className="h-16 lg:hidden" aria-hidden="true" />}

      {/* ===== FLOATING GREEN CART ===== */}
      {totalItems > 0 && (
        <div className="lg:hidden fixed left-0 right-0 z-40 px-4 pb-2" style={{ bottom: 'calc(68px + env(safe-area-inset-bottom, 0px))' }} data-testid="blinkit-floating-cart">
          <div className="max-w-3xl mx-auto">
            <Link to="/cart" className="block">
              <div className="bg-[#2D8C3C] text-white rounded-2xl px-5 py-3 flex items-center justify-between shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                    <ShoppingCart className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="font-bold text-sm">View cart</span>
                    <p className="text-xs opacity-80">{totalItems} item{totalItems > 1 ? 's' : ''}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-base">{curr}{totalAmount.toFixed(0)}</span>
                  <ChevronRight className="w-5 h-5" />
                </div>
              </div>
            </Link>
          </div>
        </div>
      )}

      {/* ===== BOTTOM NAV ===== */}
      <BlinkitBottomNav />
    </div>
  );
};

/* ========================================== */
/*  Product Card - Carousel (Blinkit)         */
/* ========================================== */
const BlinkitProductCarousel = ({ product, curr, getItemQuantity, onAdd, onAddBase, onUpdate, storeSettings, autoPlay }) => {
  const hasVariants = product.variants && product.variants.length > 0;
  const defaultVariant = hasVariants ? (product.variants.find(v => v.is_default) || product.variants[0]) : null;
  const [selectedVariant, setSelectedVariant] = useState(defaultVariant);
  const [pickerOpen, setPickerOpen] = useState(false);
  const productOos = isUnavailable(product, storeSettings);
  const comingSoon = isComingSoon(product, storeSettings);
  const activeVariant = selectedVariant || defaultVariant;
  const displayPrice = activeVariant ? activeVariant.price : product.price;
  const displayMrp = activeVariant ? activeVariant.mrp : product.mrp;
  const discount = displayMrp > displayPrice ? Math.round((1 - displayPrice / displayMrp) * 100) : 0;
  const quantity = hasVariants ? (activeVariant ? getItemQuantity(product.id, activeVariant.label) : 0) : getItemQuantity(product.id);
  const cartKey = hasVariants && activeVariant ? `${product.id}__${activeVariant.label}` : product.id;

  return (
    <div className="blinkit-product-card" data-testid={`blinkit-carousel-${product.id}`}>
      {discount > 0 && <div className="absolute top-1.5 left-1.5 z-10 bg-blue-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">{discount}% OFF</div>}
      <div className="w-full aspect-square bg-gray-50 relative flex items-center justify-center overflow-hidden">
        <ProductImageCarousel product={product} autoPlay={autoPlay} />
        {productOos && !comingSoon && <UnavailableBadge />}
      </div>
      <div className="p-2.5 flex flex-col flex-1">
        <h4 className="text-xs font-semibold text-[#1A1A1A] line-clamp-2 leading-tight mb-1">{product.name}</h4>
        {product.description && <p className="text-[10px] text-gray-400 line-clamp-1 mb-0.5">{product.description}</p>}
        <div className="flex items-center gap-1 mb-1.5">
          <p className="text-[10px] text-gray-400">{activeVariant ? activeVariant.label : product.unit}</p>
          {hasVariants && !productOos && !comingSoon && (
            <button onClick={(e) => { e.stopPropagation(); setPickerOpen(true); }}
              className="flex items-center text-[9px] font-semibold text-blue-600"
              data-testid={`change-variant-${product.id}`}>
              Change <ChevronDown className="w-2.5 h-2.5" />
            </button>
          )}
        </div>
        <VariantPicker open={pickerOpen} onOpenChange={setPickerOpen} productId={product.id} productName={product.name}
          variants={product.variants} selectedVariant={activeVariant} onSelect={setSelectedVariant}
          currencySymbol={curr} getItemQuantity={getItemQuantity} storeSettings={storeSettings} />
        <div className="flex items-center justify-between mt-auto">
          <div>
            <span className="text-sm font-bold text-[#1A1A1A]">{curr}{displayPrice}</span>
            {discount > 0 && <span className="text-[10px] text-gray-400 line-through ml-1">{curr}{displayMrp}</span>}
          </div>
          {comingSoon ? <ComingSoonButton size="xs" testId={`coming-soon-c-${product.id}`} /> : !productOos && (quantity === 0 ? (
            <button onClick={() => hasVariants ? onAdd(activeVariant) : onAddBase?.()}
              disabled={hasVariants && activeVariant && isVariantUnavailable(activeVariant, storeSettings)}
              className="blinkit-add-btn" data-testid={`blinkit-add-c-${product.id}`}><Plus className="w-4 h-4" /></button>
          ) : (
            <div className="blinkit-qty-ctrl">
              <button onClick={() => onUpdate(cartKey, quantity - 1)}><Minus className="w-3 h-3" /></button>
              <span>{quantity}</span>
              <button onClick={() => onUpdate(cartKey, quantity + 1)}><Plus className="w-3 h-3" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ========================================== */
/*  Product Card - Grid (Blinkit)             */
/* ========================================== */
const BlinkitProductGrid = ({ product, curr, getItemQuantity, onAdd, onAddBase, onUpdate, storeSettings, autoPlay }) => {
  const hasVariants = product.variants && product.variants.length > 0;
  const defaultVariant = hasVariants ? (product.variants.find(v => v.is_default) || product.variants[0]) : null;
  const [selectedVariant, setSelectedVariant] = useState(defaultVariant);
  const [pickerOpen, setPickerOpen] = useState(false);
  const productOos = isUnavailable(product, storeSettings);
  const comingSoon = isComingSoon(product, storeSettings);
  const activeVariant = selectedVariant || defaultVariant;
  const displayPrice = activeVariant ? activeVariant.price : product.price;
  const displayMrp = activeVariant ? activeVariant.mrp : product.mrp;
  const discount = displayMrp > displayPrice ? Math.round((1 - displayPrice / displayMrp) * 100) : 0;
  const quantity = hasVariants ? (activeVariant ? getItemQuantity(product.id, activeVariant.label) : 0) : getItemQuantity(product.id);
  const cartKey = hasVariants && activeVariant ? `${product.id}__${activeVariant.label}` : product.id;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden relative" data-testid={`blinkit-grid-${product.id}`}>
      {discount > 0 && <div className="absolute top-2 left-2 z-10 bg-blue-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">{discount}% OFF</div>}
      <div className="aspect-square bg-gray-50 relative flex items-center justify-center overflow-hidden">
        <ProductImageCarousel product={product} autoPlay={autoPlay} />
        {productOos && !comingSoon && <UnavailableBadge />}
      </div>
      <div className="p-2.5">
        <h4 className="text-xs font-semibold text-[#1A1A1A] line-clamp-2 leading-tight mb-1">{product.name}</h4>
        {product.description && <p className="text-[10px] text-gray-400 line-clamp-1 mb-0.5">{product.description}</p>}
        <div className="flex items-center gap-1 mb-1.5">
          <p className="text-[10px] text-gray-400">{activeVariant ? activeVariant.label : product.unit}</p>
          {hasVariants && !productOos && !comingSoon && (
            <button onClick={(e) => { e.stopPropagation(); setPickerOpen(true); }}
              className="flex items-center text-[9px] font-semibold text-blue-600"
              data-testid={`change-variant-${product.id}`}>
              Change <ChevronDown className="w-2.5 h-2.5" />
            </button>
          )}
        </div>
        <VariantPicker open={pickerOpen} onOpenChange={setPickerOpen} productId={product.id} productName={product.name}
          variants={product.variants} selectedVariant={activeVariant} onSelect={setSelectedVariant}
          currencySymbol={curr} getItemQuantity={getItemQuantity} storeSettings={storeSettings} />
        <div className="flex items-center justify-between">
          <div>
            <span className="text-sm font-bold text-[#1A1A1A]">{curr}{displayPrice}</span>
            {discount > 0 && <span className="text-[10px] text-gray-400 line-through ml-1">{curr}{displayMrp}</span>}
          </div>
          {comingSoon ? <ComingSoonButton size="xs" testId={`coming-soon-g-${product.id}`} /> : !productOos && (quantity === 0 ? (
            <button onClick={() => hasVariants ? onAdd(activeVariant) : onAddBase?.()}
              disabled={hasVariants && activeVariant && isVariantUnavailable(activeVariant, storeSettings)}
              className="blinkit-add-btn" data-testid={`blinkit-add-g-${product.id}`}><Plus className="w-4 h-4" /></button>
          ) : (
            <div className="blinkit-qty-ctrl">
              <button onClick={() => onUpdate(cartKey, quantity - 1)}><Minus className="w-3 h-3" /></button>
              <span>{quantity}</span>
              <button onClick={() => onUpdate(cartKey, quantity + 1)}><Plus className="w-3 h-3" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ========================================== */
/*  Bottom Nav (Blinkit)                      */
/* ========================================== */
const BlinkitBottomNav = () => {
  const loc = typeof window !== 'undefined' ? window.location.pathname : '/';
  const items = useCustomerNavItems();

  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }} data-testid="blinkit-bottom-nav">
      <div className="max-w-3xl mx-auto flex items-center justify-around py-2">
        {items.map(item => {
          const active = isNavItemActive(item.path, loc);
          return (
            <Link key={item.path} to={item.path}
              className={`flex flex-col items-center gap-0.5 px-3 py-1 relative transition-colors ${active ? 'text-[#E5A100]' : 'text-gray-400'}`}
              data-testid={`blinkit-bnav-${item.label.toLowerCase().replace(' ', '-')}`}>
              <item.icon className="w-5 h-5" />
              <span className="text-[10px] font-medium">{item.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export default BlinkitHome;
